import {
  type Tel,
  type SpanData,
  type StartSpanOpts,
  type ResourceUserInfo,
  type CreateEventData,
  type Span,
  type WithTel,
  getBaggage,
  getLogBaggage,
  type TelParent,
  type SpanType,
} from './tel-types.js'
import { PREPARE_FOR_LOGS_DEFAULT_OPTIONS, prepareForLog } from './prepare-for-log/prepare-for-log.js'
import { ulid } from '@w/utils'
import { makeTelStorage, type TelStorageConfig } from './tel-storage.js'
import { prepareTelError } from './prepare-for-log/prepare-tel-error.js'
import { isClient } from './utils.js'
import { hashString } from '@w/utils'

export const _telStorage = makeTelStorage()
export const setTelStorageHandler = _telStorage.setHandlers

// User data management API
export function setTelUserData(data: ResourceUserInfo): void {
  _telStorage.userDataStore.set(data)
}

export function updateTelUserData(data: Partial<ResourceUserInfo>): void {
  _telStorage.userDataStore.update(data)
}

export function getTelUserData(): ResourceUserInfo {
  return _telStorage.userDataStore.get()
}

export function subscribeTelUserData(callback: (data: ResourceUserInfo) => void): () => void {
  return _telStorage.userDataStore.subscribe(callback)
}

export function setTelUserDataSubscription(userDataConfig: {
  initialData: ResourceUserInfo
  subscribeToUserDataChange: (cb: (data: ResourceUserInfo) => void) => () => void
}): () => void {
  // Set the initial user data in the store
  _telStorage.userDataStore.set(userDataConfig.initialData)

  // Subscribe to user data changes and update the store
  // Only subscribe if initial data doesn't have userId/entityUserId
  if (!userDataConfig.initialData.userId && !userDataConfig.initialData.entityUserId) {
    let hasUpdated = false
    return userDataConfig.subscribeToUserDataChange((data) => {
      if (!hasUpdated && (data.userId || data.entityUserId)) {
        hasUpdated = true
        _telStorage.userDataStore.update(data)
      }
    })
  }

  // Return empty unsubscribe function if no subscription is needed
  return () => {}
}

export function makeTrpcTel(
  opts: Pick<SpanData, 'resourceName' | 'resourceVersion' | 'type' | 'input'> & {
    path: string
    requestData: WithTel
  },
) {
  let traceId = ''
  let parentId = ''
  let requestId = ''
  let sessionId = ''
  let id = ulid()

  const inputTel = opts.requestData?.tel // requestData shouldn't be undefined when telLink is used but let's be safe
  if (inputTel) {
    traceId = inputTel.traceId || ''
    parentId = inputTel.parentId || ''
    requestId = inputTel.requestId || ''
    sessionId = inputTel.sessionId || ''
  } else {
    traceId = ulid() // upstream tel is not provided, assume it's a new trace
    id = traceId
    requestId = traceId
  }

  const endpointSpan = _telStorage.spans.create(
    {
      id,
      name: opts.path,
      target: 'trpc-handler',
      resourceName: opts.resourceName,
      resourceVersion: opts.resourceVersion,
      type: opts.type,
      input: opts.input,
      origin: 'server', // Explicitly set server origin for tRPC handlers
    },
    {
      id: parentId,
      traceId,
      requestId,
      sessionId,
    },
  )
  const tel = makeTel(endpointSpan)
  return {
    tel,
    identifyUserForTel: (
      data: Pick<SpanData, 'userId' | 'entityUserId' | 'companyId' | 'entityId'>,
    ) => {
      tel._updateSpan(data)
    },
  }
}

export function makeJobTel(
  tel: Tel,
  opts: Pick<SpanData, 'name' | 'resourceName' | 'resourceVersion' | 'input'>,
  parent?: TelParent,
) {
  if (tel === nullTel) return nullTel

  const id = ulid()
  const jobSpan = _telStorage.spans.create(
    {
      ...opts,
      id: ulid(),
      traceId: parent ? undefined : id,
      sessionId: parent ? undefined : id,
      name: 'job',
      origin: 'server',
    },
    parent,
  )
  return makeTel(jobSpan)
}

/**
 * Creates a telemetry span for Hono HTTP handlers.
 *
 * Note: Due to Hono's middleware execution order, routePattern is typically not
 * available and spans will use the actual request path instead.
 */
export function makeHonoTel(opts: {
  method: string
  path: string
  url: string
  resourceName?: string
  resourceVersion?: string
  headers?: Record<string, string | undefined>
  input?: unknown
}) {
  // Create span name: "METHOD route-pattern" or fallback to "METHOD path"
  const spanName = `${opts.method} ${opts.path}`

  // Create display name: full URL path with query params
  const url = new URL(opts.url)
  const displayName = url.pathname + url.search

  // Determine span type based on HTTP method
  const type: SpanType = ['GET', 'HEAD', 'OPTIONS'].includes(opts.method.toUpperCase())
    ? 'query'
    : 'mutation'

  const id = ulid()

  const honoSpan = _telStorage.spans.create(
    {
      id,
      name: spanName,
      displayName: displayName,
      target: 'server-handler',
      type: type,
      input: opts.input,
      resourceName: opts.resourceName,
      resourceVersion: opts.resourceVersion,
      origin: 'server', // Explicitly set server origin for Hono handlers
      attributes: {
        'http.method': opts.method,
        'http.url': opts.url,
        'http.host': url.host,
        'http.target': url.pathname + url.search,
        // Add request headers as attributes
        ...Object.entries(opts.headers || {}).reduce(
          (acc, [key, value]) => {
            if (value) {
              // Convert header name to attribute format (kebab-case to snake_case)
              const attributeKey = `http.request.header.${key.toLowerCase().replace(/-/g, '_')}`
              acc[attributeKey] = value
            }
            return acc
          },
          {} as Record<string, string>,
        ),
      },
    },
    {
      traceId: id, // Create new trace since no upstream telemetry
      requestId: id,
    },
  )

  const tel = makeTel(honoSpan)

  return {
    tel,
  }
}

/**
 * The composition root's one call: configure the process's telemetry and open
 * the root span.
 *
 * Every CONFIGURATION key is destructured out, and only `spanData` is spread
 * into the span. What is left has to be exactly the span fields, because the
 * root span is exported like any other: spreading the whole opts object put the
 * service's `sensitiveKeys` list and its `errorMapper` FUNCTION into exported
 * span data - a config leak in the one span every trace hangs off. Adding a key
 * to the signature without adding it here brings that back, which is what the
 * `not.toHaveProperty` test over the full key set is for.
 */
export function makeRootTel({
  sensitiveKeys,
  storageConfig,
  errorFilterStrings,
  errorMapper,
  ...spanData
}: Pick<SpanData, 'resourceName' | 'resourceVersion' | 'userAgent'> & {
  sensitiveKeys?: string[]
  storageConfig?: TelStorageConfig
  errorFilterStrings?: string[] // ideally you should produce generic domain errors to encourage users to contact support or give them at least some information. this is a shortcut to silence an error but it's not a good idea to do at scale.
  errorMapper?: (error: unknown) => unknown
} = {}) {
  if (sensitiveKeys) {
    PREPARE_FOR_LOGS_DEFAULT_OPTIONS.sensitiveKeys = sensitiveKeys
  }

  // Update storage configuration including error filter strings and error mapper
  const config = {
    ...storageConfig,
    ...(errorFilterStrings && { errorFilterStrings }),
    ...(errorMapper && { errorMapper }),
  }
  if (Object.keys(config).length > 0) {
    _telStorage.updateConfig(config)
  }

  const id = ulid()
  const rootSpan = _telStorage.spans.create({
    ...spanData,

    id,
    sessionId: id,
    name: `root-${isClient ? 'client' : 'server'}`,
    origin: isClient ? 'client' : 'server', // Set origin based on environment for root spans
  })
  rootTel = makeTel(rootSpan)
}

export function makeRouteTel(pathname: string) {
  const id = ulid()

  const rootTelData = getRootTel().span.data

  const previousRouteTel = getRouteTel()
  let previousId = previousRouteTel.span.data.id
  if (previousRouteTel) {
    previousRouteTel.span.end({})
    previousId = previousRouteTel.span.data.id
  }

  const routeSpan = _telStorage.spans.create(
    {
      id,
      traceId: id,
      name: pathname,
      target: 'router',
      previousId,
      origin: 'client', // Explicitly set client origin for route spans
      resourceName: rootTelData.resourceName,
      resourceVersion: rootTelData.resourceVersion,
      // User data will be automatically populated from the user data store
    },
    {
      sessionId: rootTelData.sessionId,
    },
  )

  routeTel = makeTel(routeSpan)
}

function makeTel(span: Span): Tel {
  const baggage = getBaggage(span.data)
  const logBaggage = getLogBaggage(span.data)
  const origin = span.data.origin // Use span's origin instead of global

  return {
    info(message, data) {
      _telStorage.logs.create({
        id: ulid(),
        message,
        level: 'info',
        origin,
        ...logBaggage,
        ...data,
      })
    },
    error(message, data) {
      _telStorage.logs.create({
        id: ulid(),
        message,
        level: 'error',
        origin,
        ...logBaggage,
        ...data,
      })
    },
    warn(message, data) {
      _telStorage.logs.create({
        id: ulid(),
        message,
        level: 'warn',
        origin,
        ...logBaggage,
        ...data,
      })
    },
    debug(message, data) {
      _telStorage.logs.create({
        id: ulid(),
        message,
        level: 'debug',
        origin,
        ...logBaggage,
        ...data,
      })
    },

    createEvent(
      eventOrKey: CreateEventData | string,
      cb?: (old?: CreateEventData) => CreateEventData,
    ) {
      _telStorage.spans.events.create(eventOrKey, cb, {
        ...logBaggage,
        origin,
      })
    },

    async startSpan(...args: a[]) {
      const [nameOrOpts, cb, input] = args
      const opts: StartSpanOpts = typeof nameOrOpts === 'string' ? { name: nameOrOpts } : nameOrOpts
      const span = _telStorage.spans.create(
        {
          ...opts,
          id: ulid(),
          origin: this.span.data.origin, // Inherit origin from parent span
          input,
        },
        this.span.data,
      )
      const tel = makeTel(span)
      try {
        const result = await (args.length === 3 ? cb(input, tel) : cb(tel))
        span.end({ output: result })
        return result
      } catch (error) {
        span.end({ error })
        throw error
      }
    },

    startDbQuery(name, builder, opts) {
      return this.startSpan({ name, target: 'db', type: 'query', ...opts }, async (tel) => {
        const query = builder.toSQL()
        tel._updateSpan({
          input: {
            sql: query.sql,
            params: prepareForLog(query.params, { maskMode: 'extreme' }),
            __preparedForLog: true,
          },
          label: hashString(query.sql),
        })
        const r = await builder.execute()
        return r
      })
    },

    startDbMutation(name, builder, opts) {
      return this.startSpan({ name, target: 'db', type: 'mutation', ...opts }, (tel) => {
        const query = builder.toSQL()
        tel._updateSpan({
          input: {
            sql: query.sql,
            params: prepareForLog(query.params, { maskMode: 'extreme' }),
            __preparedForLog: true,
          },
          label: hashString(query.sql),
        })
        return builder.execute()
      })
    },

    _updateSpan(patch) {
      this.span.update(patch)
    },

    onCatch(error, _extra) {
      // Apply error mapper if configured
      const errorMapper = _telStorage.getErrorMapper ? _telStorage.getErrorMapper() : undefined
      const mappedError = errorMapper ? errorMapper(error) : error

      const err = prepareTelError({
        error: mappedError,
        span: this.span.data,
      })

      // Check if it's a domain error and downgrade to warning
      if (err.type === 'domain') {
        err.level = 'warning'
      }

      // Check if error message contains any filter strings and downgrade to warning
      const errorFilterStrings = _telStorage.getErrorFilterStrings()
      if (errorFilterStrings.length > 0 && err.message) {
        for (const filterString of errorFilterStrings) {
          if (err.message.includes(filterString)) {
            err.level = 'warning'
            break
          }
        }
      }

      // _telStorage.spans.update({ id: this.span.data.id, error: err, status: 'error' }) // onCatch is used for graceful degradation, not for span termination
      _telStorage.logs.create({
        id: ulid(),
        message: err.message,
        level: 'error',
        ...logBaggage,
        origin,
        error: err,
      }) // 1 span can have multiple errors
      _telStorage.spans.errors.create(mappedError, err)
      return error
    },

    _createSpan(opts) {
      return _telStorage.spans.create(
        { ...opts, id: opts.id || ulid(), origin: this.span.data.origin },
        this.span.data,
      )
    },

    _createRequestSpan(opts) {
      const id = ulid()
      return _telStorage.spans.create(
        { ...opts, id, requestId: id, origin: this.span.data.origin },
        this.span.data,
      )
    },

    _createTel(opts) {
      return makeTel(this._createSpan(opts))
    },

    _clear() {
      _telStorage.clear()
    },

    async flush() {
      await _telStorage.flush()
    },

    _registerFlush(callback: () => Promise<void>) {
      _telStorage.registerFlush(callback)
    },

    span,

    parentId: baggage.parentId,
    traceId: baggage.traceId,
    requestId: baggage.requestId,
    sessionId: baggage.sessionId,
  }
}

export const nullTel: Tel = {
  info: () => {},
  error: () => {},
  warn: () => {},
  debug: () => {},

  startSpan: (...args: a[]) => {
    const [_, cb, input] = args
    return args.length === 3 ? cb(input, nullTel) : cb(nullTel)
  },
  _updateSpan: () => {},
  _createSpan: () => nullTel.span,
  _createRequestSpan: () => nullTel.span,
  _createTel: () => nullTel,

  _clear: () => {},
  flush: () => Promise.resolve(),
  _registerFlush: () => {},
  createEvent: () => {},
  startDbQuery: (_, query) => query.execute(),
  startDbMutation: (_, query) => query.execute(),
  onCatch: (e) => e,

  span: {
    data: { id: '', name: '', target: 'router' },
    update: () => {},
    end: () => {},
    // Nothing is recorded here at all, so a producer asking whether to build an
    // output gets the answer that saves it the work.
    capturesOutput: () => false,
    _getBaggage: () => ({}),
  },
}

// i don't think it makes sense to try to isolate rootTel so it has proper origin even if server runs in browser. It's too difficult and doesn't give any benefits.
// using globalThis.rootTel: Tel led to weird ts errors. had to use ts-ignore. Let's try this approach.
let rootTel: Tel | undefined
export const getRootTel = () => rootTel || nullTel
let routeTel: Tel | undefined
export const getRouteTel = () => routeTel || nullTel

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any

// for context propagation tel should be passed explicitly.
// Cons: argument drill. Mitigation: use instrumentation. dizzle is not possible to instrument. instrumentation can be also challenging.
// for node js we have AsyncLocalStorage that allows asynchronous context propagation.
// However, its best polyfill for browser (https://github.com/langchain-ai/langgraphjs/issues/879#issuecomment-2749252465)
// has the next issues: Parallel and Nested async function Runs are not supported. Basically, it's useless because this are very common use cases.
// Opentelemetry uses deprecated angular package that patches a lot of browser apis to make async propagation work https://github.com/angular/angular/tree/main/libs/zone.js#development-status-of-zonejs
